import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { clientService } from "../../services/clients";
import toast from "react-hot-toast";

const ClientForm = ({ client, onSuccess }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: client || {},
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      reset({
        ...client,
        servicesAvailed: client.servicesAvailed?.join(", ") || "",
        contractStart: client.contractStart?.split("T")[0] || "",
        contractEnd: client.contractEnd?.split("T")[0] || "",
      });
    } else {
      reset({});
    }
  }, [client, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      ...data,
      servicesAvailed: data.servicesAvailed
        ? data.servicesAvailed.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try {
      if (client) {
        await clientService.update(client._id, payload);
        toast.success("Client updated");
      } else {
        await clientService.create(payload);
        toast.success("Client created");
      }
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        {...register("name", { required: "Name is required" })}
        error={errors.name?.message}
      />
      <Input
        label="Phone"
        {...register("phone", { required: "Phone is required" })}
        error={errors.phone?.message}
      />
      <Input
        label="Email"
        type="email"
        {...register("email", { required: "Email is required" })}
        error={errors.email?.message}
      />
      <Input label="Company" {...register("company")} />
      <div>
        <label className="block text-sm font-medium mb-1">Business Type</label>
        <select
          {...register("businessType")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Business Type</option>
          <option value="local">Local</option>
          <option value="SME">SME</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>
      <Input
        label="Services Availed (comma separated)"
        {...register("servicesAvailed")}
      />
      <Input
        label="Contract Start"
        type="date"
        {...register("contractStart")}
      />
      <Input
        label="Contract End"
        type="date"
        {...register("contractEnd")}
      />
      <Input
        label="Contract Value"
        type="number"
        {...register("contractValue")}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Payment Status</label>
        <select
          {...register("paymentStatus")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Payment Status</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
      <Input
        label="Health Score (0-100)"
        type="number"
        {...register("healthScore", { min: 0, max: 100 })}
      />
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : client ? "Update" : "Create"}
      </Button>
    </form>
  );
};

export default ClientForm;
